import React, { useEffect, useState, useCallback } from 'react';
import {
  collection, getDocs, addDoc, doc, updateDoc, deleteDoc, Timestamp
} from 'firebase/firestore';
import { db } from '../components/firebase';
import Auth from '../components/Auth';

const ADMIN_EMAIL = 'adamlehavi@gmail.com';

const BOOK_CATEGORIES = [
  'Classic Literature',
  'Modern Fiction',
  'Postmodern',
  'Business',
  'Pop Science',
  'Biography & Memoir',
  'Philosophy & Society',
];

const SHELF_TOTAL = 70;
const HCSOL_COUNT = 50;

const emptyBook = { name: '', author: '', categoryV2: BOOK_CATEGORIES[0], x: 0.5, y: 0.5, size: 200, review: '' };
const emptyPost = { title: '', city: '', date: '', content: '', tags: 'public', images: '' };

const inputStyle = { width: '100%', marginBottom: '0.5rem', padding: '0.4rem' };
const rowStyle = { display: 'flex', gap: '0.5rem', alignItems: 'center' };

// ---------- Books ----------

function BookForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="note-box" style={{ marginBottom: '1rem' }}>
      <label>Title</label>
      <input style={inputStyle} value={form.name} onChange={set('name')} />
      <label>Author</label>
      <input style={inputStyle} value={form.author} onChange={set('author')} />
      <label>Category</label>
      <select style={inputStyle} value={form.categoryV2} onChange={set('categoryV2')}>
        {BOOK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <div style={rowStyle}>
        <div style={{ flex: 1 }}>
          <label>Liked (x, 0–1)</label>
          <input style={inputStyle} type="number" step="0.01" min="0" max="1" value={form.x} onChange={set('x')} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Suggest (y, 0–1)</label>
          <input style={inputStyle} type="number" step="0.01" min="0" max="1" value={form.y} onChange={set('y')} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Pages</label>
          <input style={inputStyle} type="number" value={form.size} onChange={set('size')} />
        </div>
      </div>
      <label>Review</label>
      <textarea style={{ ...inputStyle, minHeight: '60px' }} value={form.review} onChange={set('review')} />
      <div style={rowStyle}>
        <button className="button" onClick={() => onSave({
          ...form,
          x: parseFloat(form.x),
          y: parseFloat(form.y),
          size: parseInt(form.size, 10),
        })}>Save</button>
        <button className="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function BooksAdmin() {
  const [books, setBooks] = useState([]);
  const [editing, setEditing] = useState(null); // doc id or 'new'
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    const snap = await getDocs(collection(db, 'books'));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setBooks(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (form, id) => {
    try {
      if (id === 'new') {
        // keep legacy category in sync for anything still reading it
        await addDoc(collection(db, 'books'), { ...form, category: form.categoryV2 });
      } else {
        await updateDoc(doc(db, 'books', id), form);
      }
      setStatus('Saved.');
      setEditing(null);
      load();
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'books', id));
      setStatus('Deleted.');
      load();
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    }
  };

  return (
    <div>
      {status && <div className="note-box">{status}</div>}
      {editing === 'new'
        ? <BookForm initial={emptyBook} onSave={(f) => save(f, 'new')} onCancel={() => setEditing(null)} />
        : <button className="button" onClick={() => setEditing('new')}>+ New Book</button>}
      {books.map((b) => (
        <div key={b.id} style={{ borderBottom: '1px solid #ddd', padding: '0.5rem 0' }}>
          {editing === b.id ? (
            <BookForm
              initial={{ ...emptyBook, ...b }}
              onSave={(f) => save(f, b.id)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div style={rowStyle}>
              <div style={{ flex: 1 }}>
                <b>{b.name}</b> — {b.author}
                <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                  {b.categoryV2 || b.category} · liked {b.x} · suggest {b.y} · {b.size} pp
                </div>
              </div>
              <button className="button" onClick={() => setEditing(b.id)}>Edit</button>
              <button className="button" onClick={() => remove(b.id, b.name)}>Delete</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- Reading shelf ----------

function ShelfAdmin() {
  const [docId, setDocId] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(collection(db, 'harvard-classics'));
      if (snap.empty) { setStatus('harvard-classics collection is empty'); return; }
      setDocId(snap.docs[0].id);
      setCompleted(snap.docs[0].data().completed || Array(SHELF_TOTAL).fill(false));
    };
    load();
  }, []);

  if (!completed) return <div>{status || 'Loading shelf…'}</div>;

  const toggle = (i) => {
    const next = [...completed];
    next[i] = !next[i];
    setCompleted(next);
  };

  const save = async () => {
    try {
      await updateDoc(doc(db, 'harvard-classics', docId), { completed });
      setStatus('Saved.');
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    }
  };

  const tile = (i) => (
    <button
      key={i}
      className="button"
      style={{
        width: '3rem',
        margin: '2px',
        opacity: completed[i] ? 1 : 0.35,
      }}
      onClick={() => toggle(i)}
    >
      {i < HCSOL_COUNT ? i + 1 : i - HCSOL_COUNT + 1}
    </button>
  );

  return (
    <div>
      {status && <div className="note-box">{status}</div>}
      <h3>Harvard Classics (1–{HCSOL_COUNT})</h3>
      <div>{completed.slice(0, HCSOL_COUNT).map((_, i) => tile(i))}</div>
      <h3>Shelf of Fiction (1–{SHELF_TOTAL - HCSOL_COUNT})</h3>
      <div>{completed.slice(HCSOL_COUNT).map((_, i) => tile(i + HCSOL_COUNT))}</div>
      <button className="button" style={{ marginTop: '1rem' }} onClick={save}>Save Shelf</button>
    </div>
  );
}

// ---------- Blog posts ----------

function postToForm(p) {
  const d = p.date?.seconds ? new Date(p.date.seconds * 1000) : (p.date ? new Date(p.date) : null);
  return {
    title: p.title || '',
    city: p.city || '',
    date: d ? d.toISOString().slice(0, 10) : '',
    content: p.content || '',
    tags: (p.tags || []).join(', '),
    images: (p.images || []).join('\n'),
  };
}

function formToPost(f) {
  return {
    title: f.title,
    city: f.city,
    date: f.date ? Timestamp.fromDate(new Date(`${f.date}T06:00:00Z`)) : null,
    content: f.content,
    tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
    images: f.images.split('\n').map((s) => s.trim()).filter(Boolean),
  };
}

function PostForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="note-box" style={{ marginBottom: '1rem' }}>
      <label>Title</label>
      <input style={inputStyle} value={form.title} onChange={set('title')} />
      <div style={rowStyle}>
        <div style={{ flex: 1 }}>
          <label>City</label>
          <input style={inputStyle} value={form.city} onChange={set('city')} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Date</label>
          <input style={inputStyle} type="date" value={form.date} onChange={set('date')} />
        </div>
      </div>
      <label>Tags (comma-separated; include "public" to make visible to everyone)</label>
      <input style={inputStyle} value={form.tags} onChange={set('tags')} />
      <label>Images (one storage path per line, e.g. kentucky/PXL_123.jpg)</label>
      <textarea style={{ ...inputStyle, minHeight: '80px' }} value={form.images} onChange={set('images')} />
      <label>Content</label>
      <textarea style={{ ...inputStyle, minHeight: '200px' }} value={form.content} onChange={set('content')} />
      <div style={rowStyle}>
        <button className="button" onClick={() => onSave(formToPost(form))}>Save</button>
        <button className="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function PostsAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    const snap = await getDocs(collection(db, 'blog-posts'));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
    setPosts(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (post, id) => {
    try {
      if (id === 'new') {
        await addDoc(collection(db, 'blog-posts'), post);
      } else {
        await updateDoc(doc(db, 'blog-posts', id), post);
      }
      setStatus('Saved.');
      setEditing(null);
      load();
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await deleteDoc(doc(db, 'blog-posts', id));
      setStatus('Deleted.');
      load();
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    }
  };

  return (
    <div>
      {status && <div className="note-box">{status}</div>}
      {editing === 'new'
        ? <PostForm initial={emptyPost} onSave={(p) => save(p, 'new')} onCancel={() => setEditing(null)} />
        : <button className="button" onClick={() => setEditing('new')}>+ New Post</button>}
      {posts.map((p) => (
        <div key={p.id} style={{ borderBottom: '1px solid #ddd', padding: '0.5rem 0' }}>
          {editing === p.id ? (
            <PostForm
              initial={postToForm(p)}
              onSave={(post) => save(post, p.id)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div style={rowStyle}>
              <div style={{ flex: 1 }}>
                <b>{p.title}</b>
                <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                  {p.city} · {p.date?.seconds ? new Date(p.date.seconds * 1000).toLocaleDateString() : ''} · tags: {(p.tags || []).join(', ') || 'none'}
                </div>
              </div>
              <button className="button" onClick={() => setEditing(p.id)}>Edit</button>
              <button className="button" onClick={() => remove(p.id, p.title)}>Delete</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- Page ----------

function Admin() {
  const [userEmail, setUserEmail] = useState(null);
  const [tab, setTab] = useState('books');

  const isAdmin = userEmail === ADMIN_EMAIL;

  return (
    <>
      <h1 className="page-title">Admin</h1>
      <Auth onLogin={setUserEmail} />

      {!isAdmin ? (
        <section className="section">
          <div className="note-box">
            {userEmail
              ? 'This account is not authorized to view this page.'
              : 'Log in to continue.'}
          </div>
        </section>
      ) : (
        <section className="section">
          <div style={{ ...rowStyle, marginBottom: '1rem' }}>
            {[['books', 'Books'], ['shelf', 'Reading Shelf'], ['posts', 'Blog Posts']].map(([key, label]) => (
              <button
                key={key}
                className="button"
                style={{ opacity: tab === key ? 1 : 0.5 }}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === 'books' && <BooksAdmin />}
          {tab === 'shelf' && <ShelfAdmin />}
          {tab === 'posts' && <PostsAdmin />}
        </section>
      )}
    </>
  );
}

export default Admin;
